import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { TextField, Select, MenuItem, Snackbar, Box } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { Save, ClipboardCopy, ChevronLeft } from "lucide-react";

import * as apiHelper from "../../../Utils/API/apiHelper";
import TextfieldInput from "../../Shared/TextField";
import TitlePage from "../../Shared/TitlePage";
import { dataService } from "../../../Services/data/data.service";
import { externalApiTypes } from "../../../Config/constants";
import "react-toastify/dist/ReactToastify.css";

// Components
import TableList from "../ModifyExternalApi/Components/ModifyExternalApiList";
import DescriptionItem from "../../Shared/DescriptionItem";
import HowToUseAPIDialog from "../ModifyExternalApi/Components/HowToUseAPIDialog";
import BoolToggle from "../../Shared/Buttons/BoolToggle";

function CreateExternalApiPage(props) {
  const classes = useStyles();
  const navigate = useNavigate();
  const GetUserId = props.Login.userid;
  const [connectionName, setConnectionName] = useState("");
  const [connectionDesc, setConnectionDesc] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [errorConnectionName, setErrorConnectionName] = useState(false);
  const [errorCompany, setErrorCompany] = useState(false);
  const [errorApiKey, setErrorApiKey] = useState(false);
  const [htConnectionName, setHtConnectionName] = useState("");
  const [htApiKey, setHtApiKey] = useState("");
  const [emptyDetails, setEmptyDetails] = useState(false);
  const [apiBodyItems, setApiBodyItems] = useState([]);
  const [apiExampleLink, setApiExampleLink] = useState("");
  const [apiExamplePoints, setApiExamplePoints] = useState("");
  const [openToast, setOpenToast] = useState(false);

  // Example body JSON
  const [exampleBody, setExampleBody] = useState({
    keyValue: "Your data here",
  });
  const [usingCustomReference, setUsingCustomReference] = useState(false); // Determines which stopwatch rows to show

  const [allExternalApiTypes, setAllExternalApiTypes] = useState([]);
  const [allUserCompanies, setAllUserCompanies] = useState([]);

  const [company, setCompany] = useState(null);
  const [helperTextCompany] = useState("");

  const [externalApiType, setExternalApiType] = useState(null);
  const [helperTextExternalApiType, setHelperTextExternalApiType] = useState("");
  const [errorExternalApiType, setErrorExternalApiType] = useState("");

  const [howToOpen, setHowToOpen] = useState(false);

  useEffect(() => {
    dataService.getAllCompaniesByUserId(props.Login.userid).then((results) => {
      setAllUserCompanies(results);
    });

    dataService.getAllExternalApiTypes().then((results) => {
      setAllExternalApiTypes(results);
      setExternalApiType(results[0]);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (externalApiType) {
      if (externalApiType.id === externalApiTypes.STOPWATCH) {
        setStopwatchRows();
      } else if (externalApiType.id === externalApiTypes.DATA) {
        setDataRows();
      } else if (externalApiType.id === externalApiTypes.NUMERIC) {
        setCountRows();
      }
    }
  }, [externalApiType]);

  const setConnectionNameState = (event) => {
    setConnectionName(event.target.value);
  };

  const setConnectionDescState = (event) => {
    setConnectionDesc(event.target.value);
  };

  const setApiKeyState = (event) => {
    setApiKey(event.target.value);
  };

  const setStopwatchRows = () => {
    let tempStopwatchItems = [
      {
        Id: 1,
        itemName: "stopwatchReference",
        itemDescription: "Reference to stop a earlier started stopwatch",
      },
      {
        Id: 2,
        itemName: "startTime",
        itemDescription: "The time the stopwatch starts",
      },
      {
        Id: 3,
        itemName: "stopTime",
        itemDescription: "The time the stopwatch stops",
      },
      {
        Id: 4,
        itemName: "keyValue",
        itemDescription: "Optional key value (can be used to link an api measurement to an order)",
      },
      {
        Id: 5,
        itemName: "singleTimer",
        itemDescription: "When true, starting a new timer will stop other running timers on this API",
      },
    ];

    setApiBodyItems(tempStopwatchItems);
    var ExampleApiValuesMapped = tempStopwatchItems.map((item) => ({ [item.itemName]: "Your data here" }));
    var NewExampleApiValues = JSON.stringify(Object.assign({}, ...ExampleApiValuesMapped), null, 2);
    setApiExamplePoints(NewExampleApiValues);
    setExampleBody(NewExampleApiValues);
  };

  const setDataRows = () => {
    let tempDataItems = [
      {
        Id: 1,
        itemName: "keyValue",
        itemDescription: "Optional key value (can be used to link an api measurement to an order)",
      },
      {
        Id: 2,
        itemName: "measurementTime",
        itemDescription: "The time the measurement is taken (e.g. dd:mm:yyyy hh:mm:ss 15/05/2025 14:30:00)",
      },
      {
        Id: 3,
        itemName: "customReference",
        itemDescription: "Optional custom reference to update measurements later",
      },
    ];

    setApiBodyItems(tempDataItems);
    var ExampleApiValuesMapped = tempDataItems.map((item) => ({ [item.itemName]: "Your data here" }));
    var NewExampleApiValues = JSON.stringify(Object.assign({}, ...ExampleApiValuesMapped), null, 2);
    setApiExamplePoints(NewExampleApiValues);
    setExampleBody(NewExampleApiValues);
  };

  const setCountRows = () => {
    let tempCountItems = [
      {
        Id: 1,
        itemName: "keyValue",
        itemDescription: "Optional key value (can be used to link an api measurement to an order)",
      },
      {
        Id: 2,
        itemName: "value",
        itemDescription: "The amount you want to pass (e.g. 5 or -3)",
      },
      {
        Id: 3,
        itemName: "measurementTime",
        itemDescription: "The time the measurement is taken (e.g. dd:mm:yyyy hh:mm:ss 15/05/2025 14:30:00)",
      },
      {
        Id: 4,
        itemName: "customReference",
        itemDescription: "Optional custom reference to update measurements later",
      },
    ];

    setApiBodyItems(tempCountItems);
    var ExampleApiValuesMapped = tempCountItems.map((item) => ({ [item.itemName]: "Your data here" }));
    var NewExampleApiValues = JSON.stringify(Object.assign({}, ...ExampleApiValuesMapped), null, 2);
    setApiExamplePoints(NewExampleApiValues);
    setExampleBody(NewExampleApiValues);
  };

  // switch between stopwatch with customReference and stopwatch with provided reference
  const handleStopwatchExampleBody = (usingCustomReference) => {
    setUsingCustomReference(usingCustomReference);
    let tempStopwatchItems = [];
    if (!usingCustomReference) {
      tempStopwatchItems = [
        {
          Id: 1,
          itemName: "stopwatchReference",
          itemDescription: "Reference to stop a earlier started stopwatch",
        },
        {
          Id: 2,
          itemName: "startTime",
          itemDescription: "The time the stopwatch starts",
        },
        {
          Id: 3,
          itemName: "stopTime",
          itemDescription: "The time the stopwatch stops",
        },
        {
          Id: 4,
          itemName: "keyValue",
          itemDescription: "Optional key value (can be used to link an api measurement to an order)",
        },
        {
          Id: 5,
          itemName: "singleTimer",
          itemDescription: "When true, starting a new timer will stop other running timers on this API",
        },
      ];
    } else {
      tempStopwatchItems = [
        {
          Id: 1,
          itemName: "customReference",
          itemDescription: "Reference to stop a earlier started stopwatch",
        },
        {
          Id: 2,
          itemName: "startTime",
          itemDescription: "The time the stopwatch starts",
        },
        {
          Id: 3,
          itemName: "stopTime",
          itemDescription: "The time the stopwatch stops",
        },
        {
          Id: 4,
          itemName: "keyValue",
          itemDescription: "Optional key value (can be used to link an api measurement to an order)",
        },
        {
          Id: 5,
          itemName: "useRefAsKey",
          itemDescription: "Optional, if true the customReference will be used as keyValue",
        },
        {
          Id: 6,
          itemName: "singleTimer",
          itemDescription: "When true, starting a new timer will stop other running timers on this API",
        },
      ];
    }

    var ExampleApiValuesMapped = tempStopwatchItems.map((item) => ({ [item.itemName]: "Your data here" }));
    var NewExampleApiValues = JSON.stringify(Object.assign({}, ...ExampleApiValuesMapped), null, 2);
    setExampleBody(NewExampleApiValues);
  };

  const setRows = (event) => {
    setApiBodyItems(event);
    var ExampleApiValuesMapped = event.map((item) => ({ [item.itemName]: "Your data here" }));
    var NewExampleApiValues = JSON.stringify(Object.assign({}, ...ExampleApiValuesMapped), null, 2);
    setApiExamplePoints(NewExampleApiValues);
  };

  const clearApiRows = () => {
    setApiBodyItems([]);
    setApiExamplePoints("");
  };

  const validate = () => {
    var validationOK = true;
    if (connectionName === "") {
      validationOK = false;
      setHtConnectionName("Please enter a connection name");
      setErrorConnectionName(true);
    } else {
      setHtConnectionName("");
      setErrorConnectionName(false);
    }

    if (company === null || company === "") {
      validationOK = false;
      setErrorCompany(true);
    }

    if (apiKey === "") {
      validationOK = false;
      setHtApiKey("Please generate an API key");
      setErrorApiKey(true);
    } else {
      setErrorApiKey(false);
      setHtApiKey("");
    }

    if (!externalApiType || externalApiType === "") {
      validationOK = false;
      setHelperTextExternalApiType("Please select an API type");
      setErrorExternalApiType(true);
    } else {
      setHelperTextExternalApiType("");
      setErrorExternalApiType(false);
    }

    if (apiBodyItems.length === 0 || apiBodyItems[0].itemName === "") {
      validationOK = false;
      setEmptyDetails(true);
    } else {
      setEmptyDetails(false);
    }

    return validationOK;
  };

  const sendToApi = () => {
    setApiExampleLink(apiHelper.ComposeUri(apiHelper.EXTERNALAPI) + apiKey);
    var ExampleApiValuesMapped = apiBodyItems.map((item) => ({ [item.itemName]: "Your data here" }));
    var NewExampleApiValues = JSON.stringify(Object.assign({}, ...ExampleApiValuesMapped), null, 2);
    setApiExamplePoints(NewExampleApiValues);
    if (validate()) {
      var items = JSON.stringify(apiBodyItems.map((item) => ({ Name: item.itemName, Description: item.itemDescription })));
      dataService.createNewApi(connectionName, connectionDesc, externalApiType.id, company.id, apiKey, items, GetUserId).then((results) => {
        navigate(`/modifyapi/${results.uniqueId}`);
      });
    }
  };

  const generateApiKey = () => {
    let key = uuidv4();
    setApiKey(key);
    setApiExampleLink(apiHelper.ComposeUri(apiHelper.EXTERNALAPI) + key);
  };

  const handleOpenToast = () => {
    setOpenToast(true);
  };

  const handleCloseToast = (reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenToast(false);
  };

  const handleCopyLink = () => {
    handleOpenToast();
    navigator.clipboard.writeText(apiExampleLink);
  };

  const handleCopyFormat = () => {
    handleOpenToast();
    navigator.clipboard.writeText(exampleBody);
  };

  // const action = (
  //     <React.Fragment>
  //         <IconButton
  //             size="small"
  //             aria-label="close"
  //             color="inherit"
  //             onClick={handleCloseToast}>
  //             <CloseIcon fontSize="small" />
  //         </IconButton>
  //     </React.Fragment>
  // );

  // const buttonStyle = {
  //     backgroundColor: "#C3996B",
  //     color: "white",
  //     width: "220px",
  //     marginRight: "5px",
  //     height: "55px",
  //     fontSize: "15px"
  // }

  const handleTutorialDialog = () => {
    setHowToOpen(true);
  };

  return (
    <React.Fragment>
      <TitlePage Text="Create a new API" backbutton={true} {...props} />
      <Box
        sx={{
          maxWidth: "90%",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          marginRight: "auto",
          marginLeft: "auto",
          marginTop: "148px",
        }}
      >
        <div className={classes.header}>
          <button className={classes.backButton} onClick={() => navigate("/externalapioverview")}>
            <ChevronLeft className={classes.backButton__icon} />
            Back to API overview
          </button>
          <h1>Create a new API</h1>
        </div>
        <div className={classes.container}>
          <div className={classes.formContainer}>
            <div className={classes.formColumn}>
              <div className={classes.projectOption}>
                <div className={classes.projectOption__labelContainer}>
                  {htConnectionName === "" ? <label className={classes.labelContainer__label}>API name</label> : <label className={classes.labelContainer__helperText}>{htConnectionName}</label>}
                </div>
                <TextfieldInput label={connectionName ? " " : "API"} InputLabelProps={{ shrink: false }} value={connectionName} action={setConnectionNameState} error={errorConnectionName} />
              </div>
              <div className={classes.projectOption}>
                <div className={classes.projectOption__labelContainer}>
                  {helperTextCompany === "" ? (
                    <label className={classes.labelContainer__label}>Select a company</label>
                  ) : (
                    <label className={classes.labelContainer__helperText}>{helperTextCompany}</label>
                  )}
                </div>
                <Select
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  displayEmpty
                  error={errorCompany}
                  helperText={helperTextCompany}
                  style={{ width: "100%" }}
                  renderValue={company !== "" ? undefined : () => "Company"}
                >
                  {/* If no company is selected, this is a placeholder */}
                  {allUserCompanies.length < 1 && (
                    <MenuItem value="">
                      <em>You are not part of a company</em>
                    </MenuItem>
                  )}
                  {/* Populate the dropdown with companies from allUserCompanies */}
                  {allUserCompanies.map((company, index) => (
                    <MenuItem key={index} value={company}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className={classes.projectOption}>
                <div className={classes.projectOption__labelContainer}>
                  {helperTextExternalApiType === "" ? (
                    <label className={classes.labelContainer__label}>Select an API-type</label>
                  ) : (
                    <label className={classes.labelContainer__helperText}>{helperTextExternalApiType}</label>
                  )}
                </div>
                <Select
                  value={externalApiType}
                  onChange={(e) => setExternalApiType(e.target.value)}
                  displayEmpty
                  error={errorExternalApiType}
                  helperText={helperTextExternalApiType}
                  style={{ width: "100%" }}
                  renderValue={externalApiType !== "" ? undefined : () => "API-type"}
                >
                  {/* If no company is selected, this is a placeholder */}
                  {allExternalApiTypes.length < 1 && (
                    <MenuItem value="">
                      <em>There are no types to choose from</em>
                    </MenuItem>
                  )}
                  {/* Populate the dropdown with companies from allUserCompanies */}
                  {allExternalApiTypes
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((apiType, index) => (
                      <MenuItem key={index} value={apiType}>
                        {apiType.name}
                      </MenuItem>
                    ))}
                </Select>
              </div>
            </div>
            <div className={classes.listDescriptionStyle}>
              <div className={classes.projectOption__labelContainer}>
                <p className={classes.labelContainer__label}>Description of your API</p>
              </div>
              <textarea className={classes.description__textarea} value={connectionDesc} onChange={(e) => setConnectionDescState(e)}></textarea>
              {/* <TextfieldInput label={connectionDesc ? " " : "Description"} InputLabelProps={{ shrink: false }} rows={3} multiline value={connectionDesc} action={setConnectionDescState} /> */}
            </div>
          </div>
          <div className={classes.listDescriptionStyle}>
            <DescriptionItem Text="Create API key" />
            <div className={classes.generateApiKeyContainer}>
              <button className={classes.generateAPIbutton} onClick={() => generateApiKey()}>
                Generate API key
              </button>
              <TextField value={apiKey} fullWidth action={setApiKeyState} placeholder="API key" variant="outlined" helperText={htApiKey} error={errorApiKey} slotProps={{ readOnly: true }} />
            </div>
          </div>
          {emptyDetails === true ? (
            <div style={{ marginTop: "2rem" }}>
              <DescriptionItem Text="Add data pointss" />
              <TableList {...props} rows={apiBodyItems} setRows={setRows} apiTypeId={externalApiType && externalApiType.id} makeSave={sendToApi} />
              <p style={{ color: "#d32f2f", fontWeight: "400", fontSize: "0.75rem", fontFamily: "Roboto, Helvetica, Arial", letterSpacing: "0.03333em" }}>Please create at least one data point.</p>
            </div>
          ) : (
            <div style={{ marginTop: "2rem" }}>
              <DescriptionItem Text="Add data points" />
              <TableList {...props} rows={apiBodyItems} setRows={setRows} apiTypeId={externalApiType && externalApiType.id} />
            </div>
          )}
          <div className={classes.buttonContainer}>
            <button className={classes.primaryIconButton} onClick={sendToApi}>
              <Save className={classes.iconButton__icon} absoluteStrokeWidth />
              Save
            </button>{" "}
          </div>
          <div style={{ marginTop: "20px" }}>
            <DescriptionItem Text="Your unique API link" />
            <div className={classes.ApiContainer} style={{ marginBottom: "15px" }}>
              {/* <TextField fullWidth disabled value={apiExampleLink} label={apiExampleLink ? " " : "API Link"} variant="outlined"
                            /> */}
              <input className={classes.apiTextField} disabled value={apiExampleLink} label={apiExampleLink ? " " : "API Link"} />

              {
                apiExampleLink === "" || apiExampleLink === null || apiExampleLink === undefined ? (
                  // <Button size='large' variant="contained" onClick={handleCopyLink} style={{ background: "#C3996B", color: 'white', marginBottom: "8px" }} startIcon={<ClipboardCopy />} content="Copy">Copy</Button>

                  <button className={classes.disabledIconButton}>
                    <ClipboardCopy className={classes.iconButton__icon} absoluteStrokeWidth />
                    Copy
                  </button>
                ) : (
                  <button className={classes.primaryIconButton} onClick={handleCopyLink}>
                    <ClipboardCopy className={classes.iconButton__icon} absoluteStrokeWidth />
                    Copy
                  </button>
                )

                // <Button disabled size='large' variant="contained" onClick={handleCopyLink} style={{ color: 'white', marginBottom: "8px" }} startIcon={<ClipboardCopy />} content="Copy">Copy</Button>
              }

              {/* <Button disabled={apiExampleLink === "" || apiExampleLink === null || apiExampleLink === undefined} size='large' variant="contained" onClick={handleCopyLink} className={classes.copyAPIbutton} startIcon={<ClipboardCopy />} content="Copy">Copy</Button> */}
            </div>
            <DescriptionItem Text="API format example" />
            <div className={classes.ApiFormatContainer}>
              {externalApiType?.id === externalApiTypes.STOPWATCH && <BoolToggle label="Use custom reference" boolValue={usingCustomReference} onChange={handleStopwatchExampleBody} />}
              <div style={{ width: "100%", display: "flex", alignItems: "end", gap: "8px" }}>
                <TextField
                  fullWidth
                  disabled
                  multiline
                  value={exampleBody}
                  variant="outlined"
                  label={exampleBody ? " " : "API Example"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: "none",
                      },
                    },
                  }}
                />
                <div className={classes.ApiFormatContainer__buttonContainer}>
                  <button className={classes.tertiaryButton} onClick={() => handleTutorialDialog()}>
                    How do I use this?
                  </button>
                  {exampleBody === "" || exampleBody === null || exampleBody === undefined ? (
                    <button className={classes.disabledIconButton}>
                      <ClipboardCopy className={classes.iconButton__icon} absoluteStrokeWidth />
                      Copy
                    </button>
                  ) : (
                    <button className={classes.primaryIconButton} onClick={handleCopyFormat}>
                      <ClipboardCopy className={classes.iconButton__icon} absoluteStrokeWidth />
                      Copy
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Box>
      <Snackbar open={openToast} autoHideDuration={3000} onClose={handleCloseToast} message="Copied to clipboard" />
      <HowToUseAPIDialog dialogOpen={howToOpen} closeDialog={() => setHowToOpen(false)} apiTypeId={externalApiType && externalApiType.id} apiUrl={apiExampleLink} apiBodyItems={apiBodyItems} />
    </React.Fragment>
  );
}

export default CreateExternalApiPage;
const useStyles = makeStyles((theme) => ({
  container: {
    boxSizing: "border-box",
    background: "#FFFFFF",
    borderRadius: "8px",
    padding: "1.5rem",
    maxWidth: "100%",
  },

  header: {
    width: "100%",
    height: "40px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    marginBottom: "16px",
  },

  backButton: {
    position: "absolute",
    top: "129px",

    display: "flex",
    alignItems: "center",
    gap: "4px",

    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "#0C0E29",
    opacity: ".8",

    border: "none",
    padding: "0px",

    transition: "opacity ease-in-out .1s",

    "&:hover": {
      opacity: "1",
      cursor: "pointer",
    },
  },

  backButton__icon: {
    height: "16px",
    width: "16px",

    strokeWidth: "1.4px",
  },

  buttonContainer: {
    marginTop: "25px",
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
  },

  primaryIconButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#C3996B",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },

  disabledIconButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#DCDCDC",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    "&:hover": {
      cursor: "not-allowed",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  formContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: "16px",

    marginBottom: "16px",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: "8px",
    },
  },

  formColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  projectOption: {
    minWidth: "240px",
    maxWidth: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  labelContainer__label: {
    fontSize: "16px",
    color: "rgb(105, 105, 105)",
    margin: "0",
  },

  labelContainer__helperText: {
    fontSize: "16px",
    color: "#D84949",
    margin: "0",
  },

  listDescriptionStyle: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    position: "relative",
  },

  description__textarea: {
    height: "100%",
    width: "100%",
    boxSizing: "border-box",
    padding: "8px",
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    backgroundColor: "transparent",
    border: "1px solid #BDBDBD",
    borderRadius: "4px",
    resize: "none",

    [theme.breakpoints.down("sm")]: {
      height: "120px",
    },
  },

  generateApiKeyContainer: {
    display: "flex",
    gap: "8px",
    flexDirection: "row",
    alignItems: "center",

    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },

  generateAPIbutton: {
    height: "40px",
    padding: "0 16px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#C3996B",

    transition: "all 0.2s ease-in-out",
    whiteSpace: "nowrap",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  copyAPIbutton: {
    background: "#C3996B !important",
    color: "white",
    height: "56px",
    borderBottomLeftRadius: "0px !important",
    borderTopLeftRadius: "0px !important",
    border: "1px solid #C3996B !important",
    transition: "0.3s",
    "&:hover": {
      background: "rgba(195, 153, 107, 0.75) !important",
    },
    "&:disabled": {
      background: "rgba(0, 0, 0, 0.12) !important",
      color: "rgba(0, 0, 0, 0.26) !important",
      border: "rgba(0, 0, 0, 0.26) 1px solid!important",
    },
  },

  ApiContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",

    border: "1px solid #989898",
    borderRadius: "4px",

    padding: "8px",
  },

  ApiFormatContainer: {
    height: "fit-content",

    display: "flex",
    flexDirection: "column",

    border: "1px solid #989898",
    borderRadius: "4px",

    padding: "8px",
  },

  ApiFormatContainer__buttonContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  tertiaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "rgba(12, 14, 41, 0.6)",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",
    whiteSpace: "nowrap",

    userSelect: "none",

    "&:hover": {
      color: "rgba(12, 14, 41)",
      backgroundColor: "#EDEDED",
      cursor: "pointer",
    },
  },

  apiTextField: {
    height: "40px",
    width: "100%",

    fontSize: "16px",

    backgroundColor: "transparent",
    border: "none",
  },

  generateApiKeyButton: {
    backgroundColor: "#C3996B",
    color: "white",
    width: "220px",
    borderRadius: "4px",
    border: "none",
    outline: "none",
    cursor: "pointer",
    marginRight: "5px",
    height: "55px",
    fontSize: "15px",
  },
}));
